import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Proposal } from '@guabi/shared';
import { api } from '@/lib/api';

export interface AskTurn {
  role: 'user' | 'assistant';
  text: string;
  /** Only on an assistant turn, and only until they are confirmed or ignored. */
  proposals?: Proposal[];
}

export interface AskReply {
  text: string;
  /**
   * Changes it worked out but did not make. Each is applied by the ordinary
   * endpoint when the person confirms it — there is no other write path.
   */
  proposals: Proposal[];
  /** The loop ran out of rounds or time; the answer is the best it could manage. */
  truncated: boolean;
}

export function useAsk(wedId: string) {
  const qc = useQueryClient();
  return useMutation<AskReply, Error, { question: string; history: AskTurn[] }>({
    mutationFn: ({ question, history }) =>
      api<AskReply>(`/weddings/${wedId}/ai/ask`, {
        method: 'POST',
        // History carries text only. A proposal already confirmed is a fact in
        // the data by then, and one ignored should not be re-litigated.
        body: JSON.stringify({
          question,
          history: history.map((t) => ({ role: t.role, text: t.text })),
          locale: navigator.language,
        }),
        // The tool loop budgets 18s of its own; this leaves room for the rest.
        timeoutMs: 28_000,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ai-usage', wedId] }),
  });
}
